import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Rendered when an error is caught — typically the generic ToolUseContent card. */
  fallback: React.ReactNode;
  /** Optional label for dev logging */
  toolName?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary for individual tool renderers.
 *
 * Catches any render-time crash inside a custom tool renderer and falls back
 * to the provided fallback (usually the generic ToolUseContent card) so the
 * rest of the message is unaffected.
 */
export class ToolRendererErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep a breadcrumb in the console without crashing anything else.
    console.warn(
      `[ToolRenderer] ${this.props.toolName ?? 'unknown'} threw:`,
      error.message,
      info.componentStack?.split('\n')[1]?.trim(),
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
